import { cx } from '@linaria/core';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { ChangesAlerts } from '../../../types';
import { goToMessage, goToNewsFromModel, goToRoom } from '../../actions/nav';
import { locales } from '../../locales/en';
import { authenticated } from '../../mfcLogic/auth';
import { isLevelModel } from '../../mfcLogic/userProperties/level';
import { ALERTSSTATE } from '../../state';
import { idToSlug } from '../../state/internal/users';
import { markAlertShown } from '../../state/state.alerts';
import { BANNER_TEXT_ENDORNMENT } from '../../utilities/bannerText';
import ModelStatusBadge from '../common/ModelStatusBadge';
import { Slide } from '../common/transitions';
import { useAlerts, useHeaderAlertsFilter } from '../Hooks/useAlerts';
import { useModelColor } from '../Hooks/useModelColor';
import { useUserSelector } from '../Hooks/useUser';
import "./AlertTooltips.linaria.module.css!=!./AlertTooltips?./AlertTooltips.linaria.module.css?css=H4sIAAAAAAAAA41UwZKjIBC971fkuHNgS4yZjM5l/4RCQCQiGCCooebftxxHA572ZD3p1zTd7/Uf7HpUWgTdNAovAxV2kHiuGsmmz69ff5ZjbJA3nrApRWGJAVQYRpzQqjJ63Bg1Q/BeEsKfB7hyRoOHSunlszFaBNt3fXs8UxSwFFwB4VhvK8KUY2ZjkAk1jguZB46H6mPYy73VSNCh4UXohQKjoK6tsv0tPYLDPIqL/C6lyk7whB9Ob+csR7Ap2lK60DLBW1flUerrGT2sF2Ufaj0BK55C8arWhjIDar2H8SeCc1Fo2Rxg6LHhQgHJGlcVr7xthkQ+uqJI0RZuvgt5f8XLDs1tkdVNisKAKRWKr/nzKF4iWNZeD/oAd8Z6BcxenJwiRq73xqco1Jh03OiHooBoqU3lsfkNQPTbYGEZBfnb3jaJoJ2vLfMhpliHFcWGAscmtwefGwSLFte9D2tnKzhMJ6uloKeV5zC3YD3bWZcbgo7w24X/sIDBVDxslUdtLm/IyY5lMpCHsdpUgxaxpLzfdTq2wjFgB0zYQaksR7xzjd/1EfWMTNvkFk2mA3NlMTubonRgMBnYKu4UHcYVKRMbBOuh0B05wKNLiZaPXm20hiS+e8Fwe1gnmhkQrRxT7mC+ziJ48fdxNEF7Zhqpx6oVlLI9Mz+jrr4q/0xRkEIx8NM6uAWbM4Ls0t4tDosWwLfrq6Up0X64lzMZ3lOU7IcaW7akj6ax+W4ZRyQE8oGg7Vp80wcYGq3cYmxWwWgcxCLYjNn5yQ9wjR/X51yzfc3YO4Iyt4L69UF7k5iUYrDCfn79+tszKvDp92tLncr3bJjewmoa7tr5Y7loUp7936+4/G/7R++N/P+CMSHScXuO5bqjbR85PcRiXezd2Fz7MbU3I3rx95z6uz3/rOcUxbnjbc3lPDb9uqyVfg13uVRNYmrTncINYwr0DXmLhEhl9zH5FKVCXLXxD3ByupANBwAA"
const CHIP_AVATAR = 24;
const CHIP_AVATAR_COMPACT = 18;
const CHIP_DISPLAY_MS = 5000;
const chipStyles = "atm_9s_1txwivl atm_ar_vrvcex atm_be_1q9ccgz atm_h_1h6ojuz atm_cx_ftgil2 atm_jb_idpfg4 atm_am_1pywi5l atm_e2_1f4h9lt atm_73_usvi9m atm_gz_1y44olf atm_h0_i2wt44 atm_lk_yh40bf atm_ll_19bvopo atm_2d_ec7qfv atm_7l_1sy7hev atm_3f_14habmv atm_5j_1tcgj5g atm_9j_tlke0l atm_vv_1q9ccgz";
const compactChipStyles = "atm_e2_gktfv atm_cx_i2wt44 atm_lk_t94yts atm_ll_ftgil2";
const textStyles = "atm_9s_1txwivl atm_ar_1bp4okc atm_fc_1h6ojuz atm_jb_idpfg4 atm_ks_15vqwwr atm_g3_kb7nvz atm_r3_1e5hqsa";
const textRowStyles = "atm_ar_vrvcex atm_h_1q9ycp6 atm_cx_1y44olf";
const nameStyles = "atm_c8_1skhajo atm_cs_1fw03zg atm_jb_idpfg4 atm_ks_15vqwwr atm_sq_1l2sidv atm_7gthy8_1fwxnve";
const statusStyles = "atm_c8_19bvopo atm_h3_t94yts atm_7l_1fs2ovw atm_ks_15vqwwr atm_sq_1l2sidv";
const statusRowStyles = "atm_h3_idpfg4 atm_am_glywfm";
const broadcastingStyles = "atm_7l_1nxixhv";
const stylesTokens = "atm_c8_1skhajo atm_cs_1fw03zg atm_g3_dlk8xv atm_ks_15vqwwr atm_sq_1l2sidv atm_7gthy8_1fwxnve";
export interface IAlertsState {
  canDisplayNext: boolean;
  current: ChangesAlerts;
  next: ChangesAlerts;
  display: boolean;
}
interface AlertTooltipsProps {
  compact?: boolean;
  stack?: boolean;
}
const AlertTooltips = ({
  compact,
  stack
}: AlertTooltipsProps) => {
  const {
    alerts: newAlerts
  } = useAlerts({
    updatesOnly: true
  });
  const isAuthenticated = authenticated();
  const allowsAlert = useHeaderAlertsFilter();
  const [alert, setAlertState] = useState({
    display: false,
    current: {},
    next: null,
    //
    // Useful for allowing the animation to fade with the current information
    //
    canDisplayNext: true
  } as IAlertsState);
  const hideTimeout = useRef(null);

  //
  // Ensure that the entries are unique and only the lastest alert for a
  // given model will canDisplayNext
  //
  const queueMap = useRef(new Map<number, ChangesAlerts>());
  const setAlert = (update: Partial<IAlertsState>) => {
    setAlertState(prev => ({
      ...prev,
      ...update
    }));
  };
  const {
    current,
    display,
    canDisplayNext,
    next
  } = alert;
  const {
    name,
    key,
    id,
    news,
    previousValue,
    currentValue
  } = current;
  const isTokensAlert = key === 'tokens';
  const level = useUserSelector(id, 'level');
  const modelColor = useModelColor(useUserSelector(id, 'color'));

  // anything the chip will not render must never reach `display`
  const renders = (alert: ChangesAlerts) => alert.key === 'tokens' || !stack && allowsAlert(alert);
  const lastQueued = (): ChangesAlerts | null => {
    const queued = Array.from(queueMap.current.values());
    return queued[queued.length - 1] ?? null;
  };
  const resolveNextItem = current => {
    setAlert({
      display: false,
      next: lastQueued(),
      current
    });
  };
  const purge = (keep: (alert: ChangesAlerts) => boolean) => {
    const kept = new Map(Array.from(queueMap.current).filter(([, queued]) => keep(queued)));
    const changed = kept.size !== queueMap.current.size;
    queueMap.current = kept;
    if (display && !keep(current)) {
      clearTimeout(hideTimeout.current);
      resolveNextItem(current);
    } else if (changed) {
      setAlert({
        next: lastQueued()
      });
    }
  };
  useEffect(() => {
    return () => {
      clearTimeout(hideTimeout.current);
    };
  }, []);
  useEffect(() => {
    if (newAlerts.length === 0) {
      // an empty emit with an empty store is a Clear; a read-only
      // reset also emits empty but leaves the store populated
      if (ALERTSSTATE.alerts.size === 0) {
        purge(alert => alert.key === 'tokens');
      }
      return;
    }
    const previouslyEmpty = queueMap.current.size === 0;
    newAlerts.forEach(alert => {
      if (!renders(alert)) return;
      const {
        id
      } = alert;
      const queued = queueMap.current.get(id);

      // A queued tokens alert not yet shown keeps its original
      // previousValue so the displayed amount spans both updates

      if (alert.key === 'tokens' && queued?.key === 'tokens') {
        queueMap.current.set(id, {
          ...alert,
          previousValue: queued.previousValue
        });
        return;
      }
      queueMap.current.set(id, alert);
    });
    if (!previouslyEmpty) {
      return;
    }
    setAlert({
      next: lastQueued()
    });
  }, [newAlerts]);
  useEffect(() => {
    purge(renders);
  }, [stack, allowsAlert]);
  useEffect(() => {
    if (alert.display || !alert.canDisplayNext || !alert.next) {
      return;
    }
    const {
      next
    } = alert;
    const {
      id
    } = alert.next;
    queueMap.current.delete(id);
    if (next.key !== 'tokens') markAlertShown(id);
    setAlert({
      canDisplayNext: false,
      display: true,
      current: next
    });
    hideTimeout.current = setTimeout(() => resolveNextItem(next), CHIP_DISPLAY_MS);
  }, [display, canDisplayNext, next]);
  const onClickAndDismissAlert = useCallback(() => {
    const slug = idToSlug[id];
    clearTimeout(hideTimeout.current);
    if (!isTokensAlert) {
      if (news) {
        goToNewsFromModel(slug);
      } else {
        if (isLevelModel(level)) goToRoom(idToSlug[id]);else goToMessage(idToSlug[id]);
      }
    }
    resolveNextItem(current);
  }, [id, news, level, isTokensAlert]);
  if (!isAuthenticated) {
    return null;
  }
  if (!display && canDisplayNext) return null;
  return <Slide onExited={() => {
    setAlert({
      canDisplayNext: true
    });
  }} in={display} direction="down">
            <div data-testid="Alert__Tooltips" key={id} onClick={onClickAndDismissAlert} className={cx(chipStyles, compact && compactChipStyles)}>
                {isTokensAlert ? <span className={stylesTokens}>
                        +{currentValue - previousValue}{' '}
                        {locales.header.tokensAdded} {BANNER_TEXT_ENDORNMENT}
                    </span> : <>
                        <ModelStatusBadge id={id} size={compact ? CHIP_AVATAR_COMPACT : CHIP_AVATAR} kind={key} news={news} addBoxShadow disableDrag />
                        <div className={cx(textStyles, compact && textRowStyles)}>
                            <span className={nameStyles} style={{
            color: modelColor
          }}>
                                {name}
                            </span>
                            <span className={cx(statusStyles, compact && statusRowStyles, key === 'isBroadcasting' && broadcastingStyles)}>
                                {news ? locales.alerts.newsUpdate : locales.alerts[key]}
                            </span>
                        </div>
                    </>}
            </div>
        </Slide>;
};
export default memo(AlertTooltips);